import * as Api from '@ViewModels';
import { getSocialMediaPlainTextContentFromRawRichContent } from './richContentExtractionUtils';
import { createContentStateWithHtmlStringValue } from './richContentUtils';
import { applyFacebookMentions, parseFacebookMentions } from './socialUtils';

/**
 * A platform's version while it is being written. The composer edits plain text the way the base
 * post does; the rich-text document is built when the post is sent.
 */
export interface IPlatformCustomizationDraft {
	platform: Api.SocialMediaType;
	content: string;
	attachments: Api.IFileAttachment[];
}

export const findPlatformCustomization = (
	customizations: readonly IPlatformCustomizationDraft[] | undefined,
	platform: Api.SocialMediaType
) => customizations?.find(customization => customization.platform === platform);

export const createCustomizationFromBase = (
	platform: Api.SocialMediaType,
	baseContent: string,
	baseAttachments: readonly Api.IFileAttachment[]
): IPlatformCustomizationDraft => ({
	platform,
	content: baseContent ?? '',
	attachments: [...(baseAttachments ?? [])],
});

export const getTargetedPlatforms = (postTargets?: readonly Api.IPostTarget[]): Api.SocialMediaType[] => {
	const providers = (postTargets ?? [])
		.map(target => target.provider)
		.filter(provider => Api.SupportedSocialMediaTypes.includes(provider));
	return Api.SupportedSocialMediaTypes.filter(platform => providers.includes(platform));
};

/**
 * The version the composer is editing. A stored selection only counts while its platform is still
 * targeted — untargeting the platform on screen must not leave edits pointed at a version that is
 * no longer sent — so it otherwise falls back to the first targeted platform, which is the tab the
 * version tabs select. Undefined when nothing is targeted.
 */
export const getActivePlatform = (
	postTargets: readonly Api.IPostTarget[] | undefined,
	selectedPlatform: Api.SocialMediaType | undefined
): Api.SocialMediaType | undefined => {
	const targetedPlatforms = getTargetedPlatforms(postTargets);
	return selectedPlatform && targetedPlatforms.includes(selectedPlatform) ? selectedPlatform : targetedPlatforms[0];
};

/**
 * Every targeted platform ends up with a complete entry, copied from the base where one is missing —
 * the server rejects a gap rather than falling back to the base, so the client fills it here.
 *
 * Entries for platforms that are no longer targeted are kept in the draft and simply not sent, so
 * untargeting a platform and targeting it again in the same session does not lose its wording.
 */
export const materializePlatformCustomizations = (
	targetedPlatforms: readonly Api.SocialMediaType[],
	existing: readonly IPlatformCustomizationDraft[] | undefined,
	baseContent: string,
	baseAttachments: readonly Api.IFileAttachment[]
): IPlatformCustomizationDraft[] => {
	const missing = targetedPlatforms.filter(platform => !findPlatformCustomization(existing, platform));
	if (!missing.length) {
		return [...(existing ?? [])];
	}
	return [
		...(existing ?? []),
		...missing.map(platform => createCustomizationFromBase(platform, baseContent, baseAttachments)),
	];
};

/**
 * The base media is the pool every platform publishes from, so a change to it flows through: media
 * removed from the pool can no longer publish anywhere, newly added media is routed, and each
 * version keeps the pool's order.
 *
 * `routeAddedTo` decides where new media lands. Once the post is split the composer names the
 * version on screen, so media added while editing the LinkedIn version publishes to LinkedIn alone
 * — which is what the user is asking for by adding it there. Left undefined (the unsplit post, and
 * every media change that is not an addition) new media starts on every version, so the pool a user
 * built before splitting still publishes everywhere.
 *
 * Routing is a starting point, never a wall: the pool stays whole on every tab and any version can
 * still pick up any of it.
 */
export const applyBaseAttachmentsToCustomizations = (
	customizations: readonly IPlatformCustomizationDraft[] | undefined,
	previousBaseAttachments: readonly Api.IFileAttachment[],
	nextBaseAttachments: readonly Api.IFileAttachment[],
	routeAddedTo?: Api.SocialMediaType
): IPlatformCustomizationDraft[] => {
	const previousIds = new Set((previousBaseAttachments ?? []).map(attachment => attachment.id));
	const addedIds = new Set(
		(nextBaseAttachments ?? []).filter(attachment => !previousIds.has(attachment.id)).map(attachment => attachment.id)
	);
	return (customizations ?? []).map(customization => {
		const routedIds = new Set(customization.attachments.map(attachment => attachment.id));
		const takesAdditions = !routeAddedTo || routeAddedTo === customization.platform;
		return {
			...customization,
			attachments: (nextBaseAttachments ?? []).filter(
				attachment => routedIds.has(attachment.id) || (takesAdditions && addedIds.has(attachment.id))
			),
		};
	});
};

/**
 * Swaps one attachment for another everywhere it appears, keeping each version's routing and each
 * pool's order.
 *
 * A replacement carries a new id, and {@link applyBaseAttachmentsToCustomizations} reads a new id as
 * an addition — so reconciling a swap through that path would drop the original from every version
 * and hand the replacement to one. A crop and an AI edit both replace rather than add, which is why
 * they need an operation that says so.
 */
export const replaceAttachmentEverywhere = (
	customizations: readonly IPlatformCustomizationDraft[] | undefined,
	originalId: string,
	replacement: Api.IFileAttachment
): IPlatformCustomizationDraft[] =>
	(customizations ?? []).map(customization => ({
		...customization,
		attachments: customization.attachments.map(attachment => (attachment.id === originalId ? replacement : attachment)),
	}));

const hasSameAttachments = (
	attachments: readonly Api.IFileAttachment[],
	baseAttachments: readonly Api.IFileAttachment[] | undefined
) => {
	const baseIds = (baseAttachments ?? []).map(attachment => attachment.id);
	return (
		attachments.length === baseIds.length && attachments.every((attachment, index) => attachment.id === baseIds[index])
	);
};

export const customizationDiffersFromBase = (
	customization: IPlatformCustomizationDraft | undefined,
	baseContent: string,
	baseAttachments: readonly Api.IFileAttachment[]
) =>
	!!customization &&
	(customization.content !== (baseContent ?? '') || !hasSameAttachments(customization.attachments, baseAttachments));

/** What going back to one post takes from a platform: its own wording, its own pick of media, or both. */
export interface IPlatformVersionChange {
	platform: Api.SocialMediaType;
	wording: boolean;
	media: boolean;
}

/**
 * Every version the draft holds that differs from the base post, targeted or not: turning the split off
 * discards all of them, including one kept for a platform that was untargeted along the way.
 */
export const getPlatformVersionChanges = (
	customizations: readonly IPlatformCustomizationDraft[] | undefined,
	baseContent: string,
	baseAttachments: readonly Api.IFileAttachment[]
): IPlatformVersionChange[] =>
	Api.SupportedSocialMediaTypes.flatMap(platform => {
		const customization = findPlatformCustomization(customizations, platform);
		if (!customization) {
			return [];
		}
		const wording = customization.content !== (baseContent ?? '');
		const media = !hasSameAttachments(customization.attachments, baseAttachments);
		return wording || media ? [{ media, platform, wording }] : [];
	});

/**
 * True while a targeted platform's version leaves out some of the post's media. The base post publishes
 * all of it everywhere, so going back to one post puts media meant for one platform on every other.
 */
export const isMediaLeftOffAPlatform = (
	customizations: readonly IPlatformCustomizationDraft[] | undefined,
	baseAttachments: readonly Api.IFileAttachment[],
	targetedPlatforms: readonly Api.SocialMediaType[]
) =>
	targetedPlatforms.some(platform => {
		const customization = findPlatformCustomization(customizations, platform);
		return (
			!!customization &&
			(baseAttachments ?? []).some(attachment => !customization.attachments.some(routed => routed.id === attachment.id))
		);
	});

export const isFacebookTargeted = (postTargets?: readonly Api.IPostTarget[]) =>
	getTargetedPlatforms(postTargets).includes(Api.SocialMediaType.Facebook);

/**
 * Only the platforms passed are sent, in their order, and only those with an entry.
 *
 * Every version is written and stored like the base body, mentions included: each carries its own
 * "@Page Name" text and each is tokenized against the same picked Pages. A version publishes to one
 * platform, so a Page mention in one cannot reach a network that would only strip it.
 */
export const toApiPlatformCustomizations = (
	customizations: readonly IPlatformCustomizationDraft[] | undefined,
	targetedPlatforms: readonly Api.SocialMediaType[],
	facebookMentions?: readonly Api.IFacebookPageLookupResult[]
): Api.IPostPlatformCustomization[] =>
	targetedPlatforms
		.map(platform => findPlatformCustomization(customizations, platform))
		.filter((customization): customization is IPlatformCustomizationDraft => !!customization)
		.map(customization => ({
			platform: customization.platform,
			content: {
				document: createContentStateWithHtmlStringValue(
					applyFacebookMentions(customization.content, facebookMentions ? [...facebookMentions] : [])
				).getRawRichTextContent().document,
			},
			attachments: customization.attachments,
		}));

/**
 * Stored versions as drafts, with every Page their tokens name. A version can mention a Page the base
 * never does, and a Page missing from the composer's picked set is saved back as plain text.
 */
export const fromApiPlatformCustomizations = (
	customizations: readonly Api.IPostPlatformCustomization[] | undefined
): { drafts: IPlatformCustomizationDraft[]; mentions: Api.IFacebookPageLookupResult[] } => {
	const mentions: Api.IFacebookPageLookupResult[] = [];
	const drafts = (customizations ?? []).map(customization => {
		const parsed = parseFacebookMentions(getSocialMediaPlainTextContentFromRawRichContent(customization.content));
		mentions.push(...parsed.mentions);
		return {
			platform: customization.platform,
			content: parsed.content,
			attachments: customization.attachments ?? [],
		};
	});
	return { drafts, mentions };
};

/**
 * What one platform publishes from a stored post or template, for read-only previews: its own version
 * once the source is split, the base otherwise. A split source with no version for the platform
 * publishes nothing there — the server fails that target rather than falling back — so it previews
 * empty instead of showing base copy that never goes out. Generic over the platform type because the
 * anonymous compliance view declares its own copy of the platform enum.
 */
export const getPublishedPlatformVersion = <TPlatform, TAttachment>(
	source:
		| {
				content?: Api.IRawRichTextContentState;
				attachments?: TAttachment[];
				platformCustomizations?: readonly {
					platform: TPlatform;
					content?: Api.IRawRichTextContentState;
					attachments?: TAttachment[];
				}[];
		  }
		| null
		| undefined,
	platform: TPlatform | undefined
): { content?: Api.IRawRichTextContentState; attachments: TAttachment[] } => {
	if (!source?.platformCustomizations?.length) {
		return { content: source?.content, attachments: source?.attachments ?? [] };
	}
	const version = source.platformCustomizations.find(customization => customization.platform === platform);
	return { content: version?.content, attachments: version?.attachments ?? [] };
};
